import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../helper/ShowToast";
import {
  trackProfessorUActivity,
  PROFESSORU_ACTIVITY_TYPES,
  PROFESSORU_MILESTONE_ACTIVITY_TYPES,
} from "../api/professoru/progress.api";
import { getCreditDetails } from "../redux/actions/billing";
import { notifyRewardCompletions } from "../redux/actions/backgroundProcesses";

const MILESTONE_TOAST_DELAY_MS = 500;

export const useProfessorUActivityTracker = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toastTimerRef = useRef(null);
  const COMPLETED_IDS_KEY = "professoru_completed_ids";

  const loadCompletedIds = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem(COMPLETED_IDS_KEY)) || {};
    } catch {
      return {};
    }
  }, []);

  const saveCompletedIds = useCallback((map) => {
    try {
      localStorage.setItem(COMPLETED_IDS_KEY, JSON.stringify(map));
    } catch {
      // ignore storage errors
    }
  }, []);

  const completedIdsRef = useRef(loadCompletedIds());

  const recordCompletion = useCallback(
    (activityType, id) => {
      if (!id) return false;
      const map = completedIdsRef.current;
      const set = new Set(map[activityType] || []);
      if (set.has(id)) return false;
      set.add(id);
      map[activityType] = Array.from(set);
      completedIdsRef.current = map;
      saveCompletedIds(map);
      return true;
    },
    [saveCompletedIds]
  );

  const getDistinctCount = useCallback(
    (activityType) => completedIdsRef.current[activityType]?.length || 0,
    []
  );

  const getActivityId = useCallback((activityType, metadata) => {
    switch (activityType) {
      case PROFESSORU_ACTIVITY_TYPES.LESSON_COMPLETED:
      case PROFESSORU_ACTIVITY_TYPES.GUIDE_READ:
      case PROFESSORU_ACTIVITY_TYPES.TALK_WATCHED:
        return metadata?.contentId;
      case PROFESSORU_ACTIVITY_TYPES.TOUR_COMPLETED:
        return metadata?.tourName || metadata?.contentId;
      case PROFESSORU_ACTIVITY_TYPES.QUESTION_POSTED:
        return metadata?.questionId;
      case PROFESSORU_ACTIVITY_TYPES.REPLY_ADDED:
        return metadata?.replyId;
      default:
        return null;
    }
  }, []);

  const getLocalMilestones = useCallback(
    (activityType, metadata) => {
      const id = getActivityId(activityType, metadata);
      if (!id) return [];
      const isNew = recordCompletion(activityType, id);
      if (!isNew) return [];

      const count = getDistinctCount(activityType);
      const milestones = [];

      const thresholds = [
        {
          milestone: PROFESSORU_MILESTONE_ACTIVITY_TYPES.COMPLETE_FIRST_LESSON,
          base: PROFESSORU_ACTIVITY_TYPES.LESSON_COMPLETED,
          threshold: 1,
        },
        {
          milestone: PROFESSORU_MILESTONE_ACTIVITY_TYPES.COMPLETE_THREE_LESSONS,
          base: PROFESSORU_ACTIVITY_TYPES.LESSON_COMPLETED,
          threshold: 3,
        },
        {
          milestone: PROFESSORU_MILESTONE_ACTIVITY_TYPES.COMPLETE_FIVE_LESSONS,
          base: PROFESSORU_ACTIVITY_TYPES.LESSON_COMPLETED,
          threshold: 5,
        },
        {
          milestone: PROFESSORU_MILESTONE_ACTIVITY_TYPES.COMPLETE_TEN_TALKS,
          base: PROFESSORU_ACTIVITY_TYPES.TALK_WATCHED,
          threshold: 10,
        },
        {
          milestone: PROFESSORU_MILESTONE_ACTIVITY_TYPES.COMPLETE_TEN_GUIDES,
          base: PROFESSORU_ACTIVITY_TYPES.GUIDE_READ,
          threshold: 10,
        },
      ];

      thresholds.forEach(({ milestone, base, threshold }) => {
        if (base === activityType && count >= threshold) {
          milestones.push({ activityType: milestone, title: milestone });
        }
      });

      // Forum milestone requires at least one question and one reply
      if (
        activityType === PROFESSORU_ACTIVITY_TYPES.QUESTION_POSTED ||
        activityType === PROFESSORU_ACTIVITY_TYPES.REPLY_ADDED
      ) {
        if (
          getDistinctCount(PROFESSORU_ACTIVITY_TYPES.QUESTION_POSTED) > 0 &&
          getDistinctCount(PROFESSORU_ACTIVITY_TYPES.REPLY_ADDED) > 0
        ) {
          milestones.push({
            activityType:
              PROFESSORU_MILESTONE_ACTIVITY_TYPES.POST_A_QUESTION_AND_REPLY,
            title: PROFESSORU_MILESTONE_ACTIVITY_TYPES.POST_A_QUESTION_AND_REPLY,
          });
        }
      }

      return milestones;
    },
    [getActivityId, getDistinctCount, recordCompletion]
  );

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const invalidateRewards = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["rewards"] });
  }, [queryClient]);

  const applyMilestonesToRewardsCache = useCallback(
    (milestones) => {
      queryClient.setQueryData(["rewards"], (oldData) => {
        if (!oldData) {
          console.log("[ProfessorU rewards cache] no cached rewards data");
          return oldData;
        }
        const modules = Array.isArray(oldData) ? oldData : oldData.data;
        if (!Array.isArray(modules)) {
          console.log("[ProfessorU rewards cache] unexpected shape", oldData);
          return oldData;
        }

        const updatedModules = modules.map((module) => {
          let newlyCompleted = 0;
          const updatedRewards = (module.rewards || []).map((reward) => {
            if (reward.isCompleted) return reward;

            const milestone = milestones.find((m) => {
              const normalize = (s) =>
                String(s || "").toLowerCase().replace(/[\s_-]/g, "");

              const milestoneActivityType =
                m.activityType || m.unlockCondition?.activityType || m;
              const milestoneTitle = m.title || m.activityType || "";

              if (
                milestoneActivityType &&
                (normalize(reward.unlockCondition?.value) ===
                  normalize(milestoneActivityType) ||
                  normalize(reward.unlockCondition?.activityType) ===
                    normalize(milestoneActivityType))
              ) {
                return true;
              }
              if (
                milestoneTitle &&
                reward.title &&
                normalize(reward.title).includes(normalize(milestoneTitle))
              ) {
                return true;
              }
              return false;
            });

            if (milestone) {
              newlyCompleted += 1;
              console.log("[ProfessorU rewards cache] matched reward", reward.title, milestone);
              return { ...reward, isCompleted: true, isUnlocked: true };
            }
            return reward;
          });

          return newlyCompleted > 0
            ? {
                ...module,
                rewards: updatedRewards,
                completedRewards:
                  (module.completedRewards || 0) + newlyCompleted,
              }
            : module;
        });

        return Array.isArray(oldData)
          ? updatedModules
          : { ...oldData, data: updatedModules };
      });
    },
    [queryClient]
  );

  const track = useCallback(
    async (activityType, metadata = {}) => {
      try {
        const result = await trackProfessorUActivity(activityType, metadata);
        console.log("[ProfessorU activity]", activityType, metadata, result);

        const localMilestones = getLocalMilestones(activityType, metadata);
        const backendMilestones = result?.milestonesReached || [];
        const milestones = [...localMilestones, ...backendMilestones];
        console.log("[ProfessorU milestones]", { localMilestones, backendMilestones, combined: milestones });

        if (milestones.length > 0) {
          applyMilestonesToRewardsCache(milestones);
          invalidateRewards();
          dispatch(notifyRewardCompletions(backendMilestones));
          // A reward just completed server-side, which may have credited AI
          // credits — refresh the sidebar balance immediately.
          dispatch(getCreditDetails());

          if (toastTimerRef.current) {
            clearTimeout(toastTimerRef.current);
          }

          toastTimerRef.current = setTimeout(() => {
            const latest = milestones[milestones.length - 1];
            const title = latest?.title || "Milestone reached";
            // showToast("success", `${title} unlocked!`);
          }, MILESTONE_TOAST_DELAY_MS);
        }

        return result;
      } catch (error) {
        // Fail silently — activity tracking should not block the user experience.
        console.error("[ProfessorU activity] tracking failed:", activityType, metadata, error);
        return { isFirstVisit: false, visitCount: 0, milestonesReached: [] };
      }
    },
    [applyMilestonesToRewardsCache, getLocalMilestones, invalidateRewards, dispatch]
  );

  const trackVideoCompleted = useCallback(
    (contentId, moduleId, contentType = "video") => {
      return track(PROFESSORU_ACTIVITY_TYPES.LESSON_COMPLETED, {
        contentId,
        moduleId,
        contentType,
      });
    },
    [track]
  );

  const trackGuideRead = useCallback(
    (contentId, moduleId, contentType = "guide") => {
      return track(PROFESSORU_ACTIVITY_TYPES.GUIDE_READ, {
        contentId,
        moduleId,
        contentType,
      });
    },
    [track]
  );

  const trackTalkWatched = useCallback(
    (contentId, moduleId, contentType = "interview") => {
      return track(PROFESSORU_ACTIVITY_TYPES.TALK_WATCHED, {
        contentId,
        moduleId,
        contentType,
      });
    },
    [track]
  );

  const trackTourCompleted = useCallback(
    (tourName, moduleId, activityType = PROFESSORU_ACTIVITY_TYPES.TOUR_COMPLETED, contentId) => {
      return track(activityType, {
        tourName,
        moduleId,
        contentId,
      });
    },
    [track]
  );

  const trackQuestionPosted = useCallback(
    (questionId) => {
      return track(PROFESSORU_ACTIVITY_TYPES.QUESTION_POSTED, { questionId });
    },
    [track]
  );

  const trackReplyAdded = useCallback(
    (questionId, replyId) => {
      return track(PROFESSORU_ACTIVITY_TYPES.REPLY_ADDED, {
        questionId,
        replyId,
      });
    },
    [track]
  );

  return {
    track,
    trackVideoCompleted,
    trackLessonCompleted: trackVideoCompleted,
    trackGuideRead,
    trackTalkWatched,
    trackTourCompleted,
    trackQuestionPosted,
    trackReplyAdded,
    PROFESSORU_ACTIVITY_TYPES,
    PROFESSORU_MILESTONE_ACTIVITY_TYPES,
  };
};

export {
  PROFESSORU_ACTIVITY_TYPES,
  PROFESSORU_MILESTONE_ACTIVITY_TYPES,
};
